import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react'
import { MaterialIcon, Texto, BBDTag } from '@gravitate-js/excalibrr'
import { Select, Tag } from 'antd'
import type { ColDef, ICellEditorParams, ICellRendererParams } from 'ag-grid-community'
import type { TerminalMappings } from '../../../../api/useSupplierReconciliation'

interface Location {
  id: string
  name: string
}

interface GridContext {
  terminalOptions: Location[]
}

const MultiSelectTerminalEditor = forwardRef<any, ICellEditorParams<Location, string, GridContext>>(
  (props: ICellEditorParams<Location, string, GridContext>, ref) => {
    // Initialize value with backward compatibility
    const getInitialValue = () => {
      let values: string[] = []

      // Check for API data structure with terminals array
      if (Array.isArray(props.data?.terminals) && props.data.terminals.length > 0) {
        values = props.data.terminals.map((t: { terminal_name: string }) => t.terminal_name).filter(Boolean)
      } else if (Array.isArray(props.value)) {
        values = props.value
      } else if (Array.isArray(props.data?.terminal_names)) {
        values = props.data.terminal_names
      } else if (props.data?.terminal_name) {
        values = [props.data.terminal_name]
      } else if (typeof props.value === 'string' && props.value) {
        values = [props.value]
      }

      // Filter out "No Mapping" so editor starts empty for unmapped items
      return values.filter((value) => value !== 'No Mapping')
    }

    const [value, setValue] = useState<string[]>(getInitialValue())

    useImperativeHandle(ref, () => ({
      getValue: () => value,
      isCancelBeforeStart: () => false,
      isCancelAfterEnd: () => false,
    }))

    useEffect(() => {
      setValue(getInitialValue())
    }, [props.value, props.data])

    const options = props.context.terminalOptions
      ? props.context.terminalOptions
          .map((opt) => ({ value: opt.name, label: opt.name }))
          .sort((a, b) => a.label.localeCompare(b.label))
      : []

    return (
      <Select
        mode='multiple'
        value={value}
        onChange={setValue}
        options={options}
        placeholder='Select Terminals'
        showSearch
        allowClear
        style={{ width: '100%' }}
        filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
      />
    )
  }
)

/**
 * Generate column definitions for terminal mappings grid
 */
export function getTerminalMappingsColumns(): ColDef[] {
  return [
    {
      headerName: 'Supplier',
      field: 'supplier_name',
      minWidth: 180,
      sortable: true,
      filter: true,
      rowGroup: true,
      editable: false,
      enableRowGroup: true,
    },
    // Source ID column removed
    {
      headerName: 'Source Name',
      field: 'source_name',
      minWidth: 200,
      sortable: true,
      filter: true,
      flex: 1,
      editable: false,
    },
    {
      headerName: 'Mapping',
      field: 'terminal_names',
      minWidth: 200,
      sortable: true,
      filter: 'agSetColumnFilter',
      flex: 1,
      editable: true,
      valueGetter: (params: ICellRendererParams) => {
        // Extract terminal names from various data structures for filtering/sorting
        let values: string[] = []

        if (Array.isArray(params.data?.terminals) && params.data.terminals.length > 0) {
          values = params.data.terminals.map((t: { terminal_name: string }) => t.terminal_name).filter(Boolean)
        } else if (Array.isArray(params.value)) {
          values = params.value
        } else if (Array.isArray(params.data?.terminal_names)) {
          values = params.data.terminal_names
        } else if (params.data?.terminal_name) {
          values = [params.data.terminal_name]
        } else if (typeof params.value === 'string' && params.value) {
          values = [params.value]
        }

        // Return array for set filter (individual selection) or null for no mapping
        return values.length > 0 ? values : ['No Mapping']
      },
      comparator: (valueA: string[], valueB: string[], nodeA, nodeB, isDescending) => {
        // Check if either value is "No Mapping"
        const isANoMapping = valueA?.length === 1 && valueA[0] === 'No Mapping'
        const isBNoMapping = valueB?.length === 1 && valueB[0] === 'No Mapping'

        // If both are "No Mapping", they're equal
        if (isANoMapping && isBNoMapping) return 0

        // For ascending: "No Mapping" should come first (return -1)
        // For descending: "No Mapping" should come last (return 1)
        if (isANoMapping) return -1
        if (isBNoMapping) return 1

        // Otherwise, sort normally by first value alphabetically
        const firstA = valueA?.[0] || ''
        const firstB = valueB?.[0] || ''
        return firstA.localeCompare(firstB)
      },
      cellEditor: MultiSelectTerminalEditor,
      cellRenderer: (params: ICellRendererParams<TerminalMappings, string, GridContext>) => {
        // Handle multiple possible data sources for backward compatibility
        let values: string[] = []

        // Check for API data structure with terminals array
        if (Array.isArray(params.data?.terminals) && params.data.terminals.length > 0) {
          values = params.data.terminals.map((t: { terminal_name: string }) => t.terminal_name).filter(Boolean)
        }
        // Check for new array format
        else if (Array.isArray(params.value)) {
          values = params.value
        }
        // Check for new field name with array
        else if (Array.isArray(params.data?.terminal_names)) {
          values = params.data.terminal_names
        }
        // Check for old single field name
        else if (params.data?.terminal_name) {
          values = [params.data.terminal_name]
        }
        // Check direct value as string
        else if (typeof params.value === 'string' && params.value) {
          values = [params.value]
        }

        // Filter out "No Mapping" from values for rendering (it's only for filtering)
        const actualValues = values.filter((name) => name !== 'No Mapping')

        if (actualValues.length > 0) {
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
              {actualValues.map((name: string, index: number) => (
                <BBDTag key={index} theme2 style={{ margin: 0 }}>
                  {name}
                </BBDTag>
              ))}
            </div>
          )
        }
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: 'var(--theme-error)',
              }}
            >
              <MaterialIcon icon='warning' size={16} />
              No Mapping
            </span>
          </div>
        )
      },
    },
  ]
}
